import { without, find, includes, map, toLower } from "lodash";
import React from "react";
import PropTypes from "prop-types";

import Link from "@/components/Link";
import Button from "antd/lib/button";
import SelectItemsDialog from "@/components/select-items-dialog/SelectItemsDialog";
import { Destination as DestinationType, UserProfile as UserType } from "@/components/proptypes";

import DestinationService, { IMG_ROOT } from "@/services/destination";
import AlertSubscription from "@/services/alert-subscription";
import { clientConfig, currentUser } from "@/services/auth";
import notification from "@/services/notification";
import ListItemAddon from "@/components/groups/ListItemAddon";
import EmailSettingsWarning from "@/components/EmailSettingsWarning";
import PlainButton from "@/components/plain-button/PlainButton";
import Tooltip from "@/components/Tooltip";
import { useTheme } from "@/components/ThemeProvider";

import CloseOutlinedIcon from "@ant-design/icons/CloseOutlined";
import Switch from "antd/lib/switch";

import "./AlertDestinations.less";

const USER_EMAIL_DEST_ID = -1;

function normalizeSub(sub) {
  if (!sub.destination) {
    sub.destination = {
      id: USER_EMAIL_DEST_ID,
      name: sub.user.email,
      icon: "DEPRECATED",
      type: "email",
    };
  }
  return sub;
}

function ListItem({ destination: { name, type }, user, unsubscribe }) {
  const canUnsubscribe = currentUser.isAdmin || currentUser.id === user.id;
  const { isDarkMode } = useTheme();

  return (
    <li className={`destination-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
      <img src={`${IMG_ROOT}/${type}.png`} className="destination-icon" alt={name} />
      <span className="flex-fill">{name}</span>
      {type === "email" && (
        <EmailSettingsWarning className="destination-warning" featureName="alert emails" mode="icon" />
      )}
      {canUnsubscribe && (
        <Tooltip title="Remove" mouseEnterDelay={0.5}>
          <PlainButton className="remove-button" onClick={unsubscribe}>
            <CloseOutlinedIcon />
          </PlainButton>
        </Tooltip>
      )}
    </li>
  );
}

ListItem.propTypes = {
  destination: DestinationType.isRequired,
  user: UserType.isRequired,
  unsubscribe: PropTypes.func.isRequired,
};

function AlertDestinations({ alertId }) {
  const [dests, setDests] = React.useState([]);
  const [subs, setSubs] = React.useState(null);
  const { isDarkMode } = useTheme();

  React.useEffect(() => {
    if (alertId) {
      Promise.all([
        DestinationService.query(),
        AlertSubscription.query({ alertId }),
      ]).then(([dests, subs]) => {
        setSubs(subs.map(normalizeSub));
        setDests(dests);
      });
    }
  }, [alertId]);

  const showAddAlertSubDialog = () => {
    SelectItemsDialog.showModal({
      width: 570,
      showCount: true,
      extraFooterContent: (
        <>
          <i className="fa fa-info-circle" aria-hidden="true" /> Create new destinations in{" "}
          <Tooltip title="Opens page in a new tab.">
            <Link href="destinations/new" target="_blank">
              Alert Destinations
            </Link>
          </Tooltip>
        </>
      ),
      dialogTitle: "Add Existing Alert Destinations",
      inputPlaceholder: "Search destinations...",
      searchItems: searchTerm => {
        searchTerm = toLower(searchTerm);
        return Promise.resolve(dests.filter(d => includes(toLower(d.name), searchTerm)));
      },
      renderItem: (item, { isSelected }) => {
        const alreadyInGroup = !!find(subs, s => s.destination.id === item.id);

        return {
          content: (
            <div className={`destination-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
              <img src={`${IMG_ROOT}/${item.type}.png`} className="destination-icon" alt={item.name} />
              <span className="flex-fill">{item.name}</span>
              <ListItemAddon isSelected={isSelected} alreadyInGroup={alreadyInGroup} deselectedIcon="fa-plus" />
            </div>
          ),
          isDisabled: alreadyInGroup,
          className: isSelected || alreadyInGroup ? "selected" : "",
        };
      },
    }).onClose(items => {
      const promises = map(items, item => subscribe(item));
      return Promise.all(promises)
        .then(() => {
          notification.success("Subscribed.");
        })
        .catch(() => {
          notification.error("Failed saving subscription.");
          return Promise.reject(null);
        });
    });
  };

  const onUserEmailToggle = sub => {
    if (sub) {
      unsubscribe(sub);
    } else {
      subscribe();
    }
  };

  const subscribe = dest => {
    const sub = { alert_id: alertId };
    if (dest) {
      sub.destination_id = dest.id;
    }

    return AlertSubscription.create(sub).then(sub => {
      setSubs([...subs, normalizeSub(sub)]);
    });
  };

  const unsubscribe = sub => {
    AlertSubscription.delete(sub)
      .then(() => {
        setSubs(without(subs, sub));
      })
      .catch(() => {
        notification.error("Failed unsubscribing.");
      });
  };

  if (!alertId) {
    return null;
  }

  const currentUserEmailSub = find(subs, {
    destination: { id: USER_EMAIL_DEST_ID },
    user: { id: currentUser.id },
  });
  const filteredSubs = without(subs, currentUserEmailSub);
  const { mailSettingsMissing } = clientConfig;

  return (
    <div className={`alert-destinations ${isDarkMode ? 'dark-mode' : ''}`} data-test="AlertDestinations">
      <Tooltip title='Click to add an existing "Alert Destination"' mouseEnterDelay={0.5}>
        <Button
          data-test="ShowAddAlertSubDialog"
          type="primary"
          size="small"
          className="add-button"
          onClick={showAddAlertSubDialog}>
          <i className="fa fa-plus f-12 m-r-5" aria-hidden="true" /> Add
        </Button>
      </Tooltip>
      <ul>
        <li className={`destination-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
          <i className="destination-icon fa fa-envelope" aria-hidden="true" />
          <span className="flex-fill">{currentUser.email}</span>
          <EmailSettingsWarning className="destination-warning" featureName="alert emails" mode="icon" />
          {!mailSettingsMissing && (
            <Switch
              size="small"
              className="toggle-button"
              checked={!!currentUserEmailSub}
              loading={!subs}
              onChange={() => onUserEmailToggle(currentUserEmailSub)}
              data-test="UserEmailToggle"
            />
          )}
        </li>
        {filteredSubs.map(s => (
          <ListItem key={s.id} unsubscribe={() => unsubscribe(s)} {...s} />
        ))}
      </ul>
    </div>
  );
}

AlertDestinations.propTypes = {
  alertId: PropTypes.any.isRequired,
};

export default AlertDestinations;
